<script setup lang="ts">
// source official docs: https://developer.mozilla.org/en-US/docs/Web/API/Media_Capture_and_Streams_API/Taking_still_photos
import { computed, onMounted, ref, watch } from 'vue'
import { trans } from '@/munio/i18n/index.js'
import MdlProgressBar from '@component/mdl/ProgressBar.vue'
import MdlButton from '@component/mdl/Button.vue'
import MSelect from '@component/Select.vue'

type DeviceOption = {
  id: string
  label: string
  data: MediaDeviceInfo
}

const emit = defineEmits<{
  (e: 'closeStream'): void
  (e: 'changeImage', val: unknown): void
}>()

const streaming = ref(false)
const stream = ref<MediaStream>()
const $video = ref<HTMLVideoElement>()

const isLoading = ref(false)
const hasImage = ref(false)

const imageSrc = ref('')
const track = ref<MediaStreamTrack>()
const imgCapture = ref<ImageCapture>()
const file = ref<File>()

const selectedCamera = ref<DeviceOption>()
const videoDevices = ref<DeviceOption[]>([])

const setIntervalId = ref<number>()

onMounted(async () => {
  isLoading.value = true
  await init()
  isLoading.value = false
})

watch(
  () => selectedCamera.value,
  async () => {
    await initializeStreamWithDevice()
  },
)

async function init() {
  try {
    await checkForDevices()
    await initializeStreamWithDevice()
  } catch (err) {
    console.error(err)
  } finally {
    setIntervalId.value = setInterval(async () => {
      await checkForDevices()
    }, 1000) as unknown as number
  }
}

async function getVideoDevices() {
  const devices: MediaDeviceInfo[] = await navigator.mediaDevices.enumerateDevices()

  return devices.filter((device: MediaDeviceInfo) => {
    return device.kind === 'videoinput' && device.deviceId && device.deviceId.length > 0
  })
}

async function checkForDevices() {
  try {
    const devices = await getVideoDevices()

    if (devices.length === 0) {
      selectedCamera.value = undefined
      stream.value = undefined
      return
    }

    videoDevices.value = devices.map((device: MediaDeviceInfo) => ({
      id: device.deviceId,
      label: device.label,
      data: device,
    }))

    if (!selectedCamera.value) {
      selectedCamera.value = videoDevices.value[0]
    }
  } catch (err) {
    console.log(err)
  }
}

async function initializeStreamWithDevice() {
  stream.value = await navigator.mediaDevices.getUserMedia({
    video: selectedCamera.value?.id
      ? {
          deviceId: { exact: selectedCamera.value?.id },
        }
      : true,
    audio: false,
  })

  if (stream.value) {
    track.value = stream.value.getVideoTracks()[0]
    imgCapture.value = new ImageCapture(track.value)
  }

  playVideoStream()
}

function playVideoStream() {
  try {
    if ($video.value) {
      // @ts-ignore
      $video.value.srcObject = stream.value
      $video.value.onloadedmetadata = () => {
        $video.value?.play()
      }
    }

    $video.value?.addEventListener(
      'canplay',
      () => {
        if (!streaming.value && $video.value) {
          streaming.value = true
        }
      },
      false,
    )
  } catch (err) {
    console.error(`An error occurred: ${err}`)
  }

  if (hasImage.value) {
    clearImage()
  }
}

async function takePicture() {
  if (!imgCapture.value) {
    return
  }

  try {
    const blob = await imgCapture.value.takePhoto()

    file.value = new File([blob], 'cameraImage.jpg', { type: 'image/jpeg' })

    imageSrc.value = URL.createObjectURL(file.value)

    hasImage.value = true
  } catch (err) {
    console.error(err)
  }
}

function emptyStream() {
  stream.value?.getTracks().forEach((track) => track.stop())
}

function save() {
  emit('changeImage', file.value)
  cancel()
}

function clearImage() {
  imageSrc.value = ''
  hasImage.value = false
}

function cancel() {
  emptyStream()

  if (setIntervalId.value) {
    clearInterval(setIntervalId.value)
  }

  emit('closeStream')
}

defineExpose({
  emptyStream,
})
</script>

<template>
  <div class="w-full flex flex-col items-center justify-center">
    <MdlProgressBar v-if="isLoading" indeterminate />

    <span v-if="isLoading" class="italic mdl-color-text--grey-600 py-8">
      {{ trans('Initializing camera') }}
    </span>

    <div class="flex flex-col gap-y-4" :class="{ hidden: isLoading }">
      <span v-if="!selectedCamera" class="italic mdl-color-text--grey-400 py-2">
        {{ trans('No camera device available. Please check your settings.') }}
      </span>

      <div>
        <h4 class="mdl-subheader m-0">{{ trans('Camera devices') }}</h4>
        <MSelect
          v-model="selectedCamera"
          value-format="object"
          :options="videoDevices"
          :disabled="hasImage || videoDevices.length === 1"
          searchable
        />
      </div>

      <div :class="{ hidden: !selectedCamera && !hasImage }">
        <div class="camera">
          <video ref="$video" :class="{ hidden: hasImage }" class="w-full h-full" />
        </div>

        <img :src="imageSrc" :class="{ hidden: !hasImage }" alt="" />
      </div>

      <div class="flex">
        <MdlButton v-if="!hasImage" raised primary @click="takePicture">{{ trans('Take picture') }}</MdlButton>
        <MdlButton v-if="hasImage" primary raised @click="save">{{ trans('Save') }}</MdlButton>
        <MdlButton v-if="hasImage" outlined @click="clearImage">{{ trans('Clear') }}</MdlButton>
        <MdlButton @click="cancel">{{ trans('Cancel') }}</MdlButton>
      </div>
    </div>
  </div>
</template>
